import React from 'react';
import { ArrowRight, Play, CheckCircle, Factory, Fish, Utensils, Trees } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const navigate = useNavigate();
  
  const features = [
    'Soluciones industriales completas',
    'Repuestos de alta calidad',
    'Asesoría técnica especializada',
    'Entrega nacional e internacional',
  ];

  const handleQuoteRequest = () => {
    navigate('/quote');
  };

  const handleViewCatalog = () => {
    navigate('/products');
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-16 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-blue-800/80 to-cyan-900/90"></div>
        <img
          src="/fotoportada.webp"
          alt="Soluciones Industriales"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 container-custom">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content */}
          <div className="space-y-6 lg:space-y-8 animate-fade-in text-white text-center sm:text-left">
            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Soluciones Industriales
                <span className="text-gradient"> Integrales</span>
                <br />
                para tu Empresa
              </h1>
              <p className="text-lg md:text-xl text-blue-100 leading-relaxed">
                Expertos en servicios, productos y repuestos para las industrias
                papelera, pesquera, alimentaria, agroindustrial y más.
                Impulsamos tu productividad con soluciones confiables.
              </p>
            </div>

            {/* Features List */}
            <div className="grid sm:grid-cols-2 gap-3 max-w-sm sm:max-w-none mx-auto sm:mx-0">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center justify-center sm:justify-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-sm md:text-base text-blue-100">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto sm:mx-0">
              <Button className="bg-white text-blue-900 hover:bg-blue-50 text-base md:text-lg px-6 md:px-8 py-3 md:py-4 shadow-xl flex-1 sm:flex-none" onClick={handleQuoteRequest}>
                Solicitar Cotización
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button variant="outline" className="border-white bg-white/10 text-white hover:bg-white hover:text-blue-900 text-base md:text-lg px-6 md:px-8 py-3 md:py-4 backdrop-blur-sm flex-1 sm:flex-none" onClick={handleViewCatalog}>
                <Play className="mr-2 w-5 h-5" />
                Ver Catálogo
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 pt-6 md:pt-8 border-t border-white/20">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">15+</div>
                <div className="text-xs md:text-sm text-blue-200">Años de Experiencia</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">500+</div>
                <div className="text-xs md:text-sm text-blue-200">Clientes Satisfechos</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">24/7</div>
                <div className="text-xs md:text-sm text-blue-200">Soporte Técnico</div>
              </div>
            </div>
          </div>

          {/* Industry Cards Overlay */}
          <div className="relative animate-slide-up hidden lg:block">
            <div className="relative z-10">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 lg:p-8 shadow-2xl border border-white/20">
                <div className="bg-white/5 rounded-xl p-4 lg:p-6">
                  <div className="grid grid-cols-2 gap-3 lg:gap-4">
                    <div className="space-y-3 lg:space-y-4">
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 lg:p-4 border border-white/20">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-500 rounded-lg flex items-center justify-center mb-2">
                          <Factory className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <div className="font-semibold text-white text-sm lg:text-base">Industria Papelera</div>
                        <div className="text-xs lg:text-sm text-blue-200">Equipos especializados</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 lg:p-4 border border-white/20">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-cyan-500 rounded-lg flex items-center justify-center mb-2">
                          <Utensils className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <div className="font-semibold text-white text-sm lg:text-base">Industria Alimentaria</div>
                        <div className="text-xs lg:text-sm text-blue-200">Certificaciones sanitarias</div>
                      </div>
                    </div>
                    <div className="space-y-3 lg:space-y-4">
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 lg:p-4 border border-white/20">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
                          <Fish className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <div className="font-semibold text-white text-sm lg:text-base">Industria Pesquera</div>
                        <div className="text-xs lg:text-sm text-blue-200">Sistemas de refrigeración</div>
                      </div>
                      <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 lg:p-4 border border-white/20">
                        <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-500 rounded-lg flex items-center justify-center mb-2">
                          <Trees className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                        </div>
                        <div className="font-semibold text-white text-sm lg:text-base">Agroindustria</div>
                        <div className="text-xs lg:text-sm text-blue-200">Mantenimiento predictivo</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Background decoration */}
            <div className="absolute -top-4 -right-4 w-56 h-56 lg:w-72 lg:h-72 bg-blue-400 rounded-full blur-3xl opacity-20"></div>
            <div className="absolute -bottom-4 -left-4 w-56 h-56 lg:w-72 lg:h-72 bg-cyan-400 rounded-full blur-3xl opacity-20"></div>
          </div>

          {/* Mobile Industry Cards - Simplified version */}
          <div className="lg:hidden mt-8 mb-8">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-white/20">
              <div className="bg-white/5 rounded-xl p-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Factory className="w-4 h-4 text-white" />
                    </div>
                    <div className="font-semibold text-white text-xs leading-tight">Papelera</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                    <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Utensils className="w-4 h-4 text-white" />
                    </div>
                    <div className="font-semibold text-white text-xs leading-tight">Alimentaria</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Fish className="w-4 h-4 text-white" />
                    </div>
                    <div className="font-semibold text-white text-xs leading-tight">Pesquera</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20 text-center">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <Trees className="w-4 h-4 text-white" />
                    </div>
                    <div className="font-semibold text-white text-xs leading-tight">Agroindustria</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
